/**
 * EmailBlockBuilder — thin compatibility wrapper around the new
 * `<RichTextEditor>` (TipTap WYSIWYG + HTML toggle).
 *
 * Why a wrapper? The two consumers (WorkflowsPage, CampaignEditorPage)
 * already hand us a `blocks` array and want a `blocks` array back, with
 * the rendered HTML embedded as an HTML comment marker (see
 * `wrapBlocksInHtml` / `extractBlocks`). Rather than touching those pages,
 * we collapse all editor content into a SINGLE `custom_html` block whose
 * `html` field carries whatever the WYSIWYG produces.
 *
 * Migration behaviour: existing multi-block documents render once via
 * `renderBlocksToHtml`, become a single `custom_html` block on first edit,
 * and from then on round-trip cleanly through the rich-text editor.
 *
 * The named exports (`emptyBlocks`, `extractBlocks`, `wrapBlocksInHtml`,
 * `blocksFromRawHtml`, `renderBlocksToHtml`, `BLOCK_MARKER_*`) are
 * preserved so other call sites keep working.
 */
import { useMemo } from "react";
import RichTextEditor from "./RichTextEditor";

export const BLOCK_MARKER_START = "<!--EVOPULSE_BLOCKS:";
export const BLOCK_MARKER_END = "-->";

const uid = () => Math.random().toString(36).slice(2, 10);

const DEFAULT_HTML = `<p>Hi {{FirstName}},</p>
<p>Thanks for getting in touch with Eyvo — really appreciate you reaching out.</p>
<p>Are you available for a quick 15-minute call this week? You can grab a time directly here: <a href="{{CalendarLink}}">{{CalendarLink}}</a></p>
<p>Best,<br/>Jan<br/>Eyvo</p>`;

export function emptyBlocks() {
  return [{ id: uid(), type: "custom_html", html: DEFAULT_HTML }];
}

// ── Serialization helpers (unchanged surface) ──────────────────────────────
export function extractBlocks(bodyHtml) {
  if (!bodyHtml) return null;
  const start = bodyHtml.indexOf(BLOCK_MARKER_START);
  if (start < 0) return null;
  const end = bodyHtml.indexOf(BLOCK_MARKER_END, start);
  if (end < 0) return null;
  try {
    const json = bodyHtml.substring(start + BLOCK_MARKER_START.length, end);
    const parsed = JSON.parse(json);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function wrapBlocksInHtml(blocks, visibleHtml) {
  const marker = BLOCK_MARKER_START + JSON.stringify(blocks) + BLOCK_MARKER_END;
  return marker + "\n" + visibleHtml;
}

export function blocksFromRawHtml(html) {
  const stripped = String(html || "").replace(/<!--[\s\S]*?-->/g, "").trim();
  if (!stripped) return emptyBlocks();
  return [{ id: uid(), type: "custom_html", html: stripped }];
}

/** Render a blocks-array down to email-safe HTML. We support legacy block
 *  types (heading/text/button/image/divider/spacer/custom_html) on read so
 *  existing emails saved before the WYSIWYG migration still display, but
 *  any *new* edit collapses everything into a single custom_html block. */
export function renderBlocksToHtml(blocks) {
  const inner = (blocks || []).map(renderBlock).join("\n");
  return (
`<div style="font-family:Arial,Helvetica,sans-serif;color:#171717;max-width:600px;margin:0 auto;padding:0 16px;">
${inner}
</div>`
  );
}

function esc(s) {
  return String(s == null ? "" : s)
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

function renderBlock(b) {
  switch (b.type) {
    case "heading": {
      const level = Math.min(3, Math.max(1, Number(b.level) || 2));
      return `<h${level} style="margin:16px 0;color:${esc(b.color || "#0A0A0A")};text-align:${esc(b.align || "left")};font-weight:700;line-height:1.25;">${esc(b.text)}</h${level}>`;
    }
    case "text":
      return `<p style="margin:12px 0;color:${esc(b.color || "#171717")};text-align:${esc(b.align || "left")};line-height:1.55;font-size:15px;">${String(b.text || "").replace(/\n/g, "<br/>")}</p>`;
    case "button": {
      const bg = esc(b.bg || "#0055FF"); const fg = esc(b.fg || "#FFFFFF");
      return `<div style="margin:18px 0;text-align:${esc(b.align || "center")};"><a href="${esc(b.url || "#")}" style="display:inline-block;background:${bg};color:${fg};padding:10px 20px;text-decoration:none;font-weight:700;font-size:14px;">${esc(b.text || "Click")}</a></div>`;
    }
    case "image": {
      const img = `<img src="${esc(b.src)}" alt="${esc(b.alt || "")}" width="${Number(b.width) || 600}" style="display:block;max-width:100%;height:auto;margin:${b.align === "center" ? "0 auto" : b.align === "right" ? "0 0 0 auto" : "0"};" />`;
      return `<div style="margin:12px 0;text-align:${esc(b.align || "center")};">${b.href ? `<a href="${esc(b.href)}">${img}</a>` : img}</div>`;
    }
    case "divider":
      return `<hr style="border:0;border-top:1px solid ${esc(b.color || "#E5E5E5")};margin:16px 0;" />`;
    case "spacer":
      return `<div style="height:${Number(b.height) || 24}px;line-height:${Number(b.height) || 24}px;">&nbsp;</div>`;
    case "custom_html":
      return String(b.html || "");
    default:
      return "";
  }
}

// ── The component itself ───────────────────────────────────────────────────

export default function EmailBlockBuilder({ blocks, onBlocksChange }) {
  // Collapse incoming `blocks` (single or multi) into one HTML doc the
  // WYSIWYG can edit. We deliberately *don't* memoise across edits — only
  // when the parent swaps the block list (e.g. switching between workflow
  // steps) — so user keystrokes flow back through `onBlocksChange`.
  const initialHtml = useMemo(() => {
    if (!blocks || blocks.length === 0) return DEFAULT_HTML;
    if (blocks.length === 1 && blocks[0].type === "custom_html") {
      return String(blocks[0].html || "");
    }
    // Mixed or legacy block doc — render once on entry; future edits will
    // be saved as a single custom_html block and round-trip cleanly.
    const inner = (blocks || []).map(renderBlock).join("\n");
    return inner || DEFAULT_HTML;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocksKey(blocks)]);

  const handleChange = (html) => {
    onBlocksChange([{ id: uid(), type: "custom_html", html: html || "" }]);
  };

  return (
    <div data-testid="email-block-builder">
      <RichTextEditor
        html={initialHtml}
        onChange={handleChange}
        placeholder="Write your email — use the Token button to insert merge fields like {{FirstName}}…"
        minHeight={360}
      />
    </div>
  );
}

/** Identity key for a blocks array — used to detect external (parent)
 *  swaps without forcing a re-mount on every keystroke. */
function blocksKey(blocks) {
  if (!blocks || blocks.length === 0) return "empty";
  if (blocks.length === 1 && blocks[0].type === "custom_html") {
    // Use the id as the cache key — inner edits don't change it.
    return `single:${blocks[0].id || "x"}`;
  }
  // Legacy multi-block — stable hash of types + ids.
  return blocks.map(b => `${b.type}:${b.id || ""}`).join("|");
}
